import { scoreTier, TIER_LABELS, type ScoreTier } from "../online/score-validation";

export type LeaderboardEntry = {
  playerId: string;
  score: number;
  tier: ScoreTier;
  occurredAt: string;
  metadata?: Record<string, string | number | boolean>;
};

const LOCAL_KEY = "zv_leaderboard";

export class LeaderboardPanel {
  private readonly root: HTMLElement;

  public constructor(host: HTMLElement) {
    this.root = document.createElement("aside");
    this.root.className = "leaderboard-panel";
    this.root.hidden = true;
    this.root.innerHTML = `<header><strong>Leaderboard</strong></header><div data-list></div>`;
    host.appendChild(this.root);
  }

  public sync(entries: readonly LeaderboardEntry[], visible: boolean): void {
    this.root.hidden = !visible;
    if (!visible) return;
    const list = this.root.querySelector<HTMLElement>("[data-list]");
    if (!list) return;
    if (entries.length === 0) {
      list.innerHTML = `<p class="summary-empty">No scores yet — extract to post.</p>`;
      return;
    }
    list.innerHTML = entries
      .map(
        (e, i) =>
          `<div class="lb-row"><span>#${i + 1}</span><span class="tier-${e.tier}">${TIER_LABELS[e.tier]}</span><strong>${e.score.toLocaleString()}</strong></div>`,
      )
      .join("");
  }

  public dispose(): void {
    this.root.remove();
  }

  public static loadLocal(): LeaderboardEntry[] {
    try {
      const raw = localStorage.getItem(LOCAL_KEY);
      if (!raw) return [];
      return JSON.parse(raw) as LeaderboardEntry[];
    } catch {
      return [];
    }
  }

  public static saveLocal(entries: LeaderboardEntry[]): void {
    localStorage.setItem(LOCAL_KEY, JSON.stringify(entries.slice(0, 20)));
  }

  public static pushLocal(
    playerId: string,
    score: number,
    metadata?: Record<string, string | number | boolean>,
  ): void {
    const tier = scoreTier(score);
    const next: LeaderboardEntry = {
      playerId,
      score,
      tier,
      occurredAt: new Date().toISOString(),
      metadata,
    };
    const merged = [...LeaderboardPanel.loadLocal(), next]
      .sort((a, b) => b.score - a.score)
      .slice(0, 20);
    LeaderboardPanel.saveLocal(merged);
  }
}

export { scoreTier, TIER_LABELS };
